import { action, computed, observable } from "mobx";
import lodash from "lodash";
import * as constants from "../../../constants/constants";
import { getValueByNs, isNumeric } from "../../../utils/utils";
import { log } from "../../../utils/console.js";
import { sendAnalytics } from "../../../utils/analytics";
import {
  getLookupValueProductLists,
  getLookupValueAddCartCTAType,
  getContextValue,
  CONTEXT_MY_PRODUCTS_PRODUCT_DATA
} from '../../../common/analytics/index';

//** @typedef {import('../../../../common/analytics/add-remove-cart').AnalyticsCartProductsEventData} AnalyticsCartProductsEventData */

export class Countable {
  @observable quantity = 1;

  @observable pendingState = constants.pendingState.empty;

  @observable countableTooltip = null;

  skipQtyValidation = false;

  tooltipTimer = null;
  
  syncWithService = lodash.debounce(() => {
    const deltaQuantity = this.quantity - this._quantity;

    if (!deltaQuantity || !this.shouldSyncAPI) {
      return;
    }

    this.changePendingState(constants.pendingState.pending.qty);
    this._syncServiceRequest(deltaQuantity);
  }, constants.debounceTime);

  getGroupProductsData(quantity) {
    return this.products.reduce(
      (result, product) => {
        result.productId.push(product.ID);
        result.productQty.push(quantity);

        return result;
      },
      {
        productId: [],
        productQty: [],
      }
    );
  }

  _syncServiceRequest = async (quantity) => {
    const productData = { productId: this.ID, productQty: quantity };
    try {
        const responseData = await this.rootStore.services.myProductsAPI.addProducts(productData);
        const responseStatus = getValueByNs(responseData, "status");
        if(responseStatus.toUpperCase() === "ERROR")  
          throw new Error("Cannot sync products quantity");
        this.changePendingState();
        this.doneQtyChange();

        const gaProductData = getContextValue(CONTEXT_MY_PRODUCTS_PRODUCT_DATA, { remove: true, defaultValue: {} });
        const gaLocation = gaProductData?.gaLocation 
          ? gaProductData.gaLocation
          : `${getLookupValueProductLists('myProducts')}|${getLookupValueAddCartCTAType('quantityControl')}`;
        const parentSkuId = gaProductData?.gaVariant ? gaProductData.gaVariant : '';
        const eventType = productData.productQty < 0 ? constants.REMOVE  : constants.ADD;
        sendAnalytics({responseData, quantity, eventType, productData: this, parentSkuId, additionalData: { gaLocation }});
        return responseData;
    } catch (error) {
        this.failQtyChange(error);
        this.changePendingState(constants.pendingState.error.qty);
        log.error(`Update product ${this.ID} failed`);
    }
  }

  constructor({ item, shouldSyncAPI = false, skipQtyValidation = false }) {
    this.quantity = item.quantity;
    this._quantity = item.quantity;
    this.skuID = item.skuID;
    this.prevQuantity = this.quantity;
    this.shouldSyncAPI = shouldSyncAPI;
    this.skipQtyValidation = skipQtyValidation;
  }

  @computed
  get isDisabled() {
    return this.pendingState.includes("pending");
  }

  remove() {
    return Promise.reject(
      new Error("[Countable store item] Not implemented remove method")
    );
  }

  @action.bound
  removeCountTooltip() {
    this.countableTooltip = null;

    clearTimeout(this.tooltipTimer);
    constants.hideEventsArr.forEach((eventType) => {
      window.removeEventListener(
        eventType,
        this.removeCountTooltip,
        eventType === "touchstart" ? { passive: true } : false
      );
    });
  }

  @action.bound
  setCountTooltip(value = null) {
    this.countableTooltip = value;

    this.tooltipTimer = setTimeout(
      this.removeCountTooltip,
      constants.notifyTime
    );
    constants.hideEventsArr.forEach((eventType) => {
      window.addEventListener(
        eventType,
        this.removeCountTooltip,
        eventType === "touchstart" ? { passive: true } : false
      );
    });
  }

  @action.bound
  syncQty() {
    if (this.isDisabled) {
      return;
    }

    if (this.quantity < 1 || !this.quantity) {
      this.remove().catch(this.failQtyChange);

      return;
    }

    if (!this.skipQtyValidation && this.quantity > constants.MAX_PRODUCT_QTY) {
      this.quantity = constants.MAX_PRODUCT_QTY;
      this.setCountTooltip({ msg: this.rootStore.labelsStore.maxLimit });
    }
    
    this.syncWithService();
    this.changePendingState();
  }

  @action.bound
  changePublicQty(newQty) {
    if (this.isDisabled) {
      return;
    }

    if (newQty === "") {
      this.quantity = newQty;
      return;
    }

    newQty = parseInt(newQty, 10);
    if (!isNumeric(newQty)) {
      return;
    }

    this.quantity = newQty;

    if (!this.skipQtyValidation && this.quantity > constants.MAX_PRODUCT_QTY) {
      this.quantity = this.prevQuantity;
      this.setCountTooltip({ msg: this.rootStore.labelsStore.maxLimit });
    }

    this.prevQuantity = this.quantity;
    this.changePendingState();
  }

  changeQty(value = 0) {
    if (this.isDisabled) {
      return;
    }

    this.quantity += value;
    this.syncQty();
  }

  @action.bound
  failQtyChange(errorMessage = "") {
    if (errorMessage) {
      log.warn(errorMessage);
    }

    this.quantity = this._quantity;
  }

  @action.bound
  doneQtyChange() {
    this._quantity = this.quantity;
  }

  @action.bound
  changePendingState(value = constants.pendingState.empty) {
    this.pendingState = value;
  }
}
